import carecentreTokens from "../../tokens";

/**
 * THE mood scale on the dealer dashboard — mirrors the API's
 * App\Support\MoodScale (and only mirrors it: when the API sends a mood's
 * `group`, that wins). Moods are GROUPED and COUNTED, never scored:
 *
 *   good        happy, content          green
 *   neutral     flat                    grey   (a quiet day — M-b)
 *   low         low                     amber
 *   distressed  anxious, distressed     red    (needs a response now — M-a)
 *
 * Every screen that colours a mood (resident card, wellbeing chart …) reads
 * the colour from the GROUP here, so the same record never looks fine on one
 * screen and worrying on the next. Unknown / empty mood → no group, not plotted.
 */

const c = carecentreTokens.color;

// Cool grey for the neutral group (brand: white + cool grey + blue).
const NEUTRAL_GREY = "#94A3B8";

export const MOOD_GROUPS = {
  good: { label: "Good", color: c.success, soft: c.successSoft, tone: "ok" },
  neutral: {
    label: "Neutral",
    color: NEUTRAL_GREY,
    soft: "#F1F5F9",
    tone: "neutral",
  },
  low: { label: "Low", color: c.warning, soft: c.warningSoft, tone: "warning" },
  distressed: {
    label: "Anxious / distressed",
    color: c.danger,
    soft: c.dangerSoft,
    tone: "danger",
  },
};

export const MOOD_GROUP_ORDER = ["good", "neutral", "low", "distressed"];

/** Picker order. */
export const MOODS = {
  happy: { label: "Happy", emoji: "😄", group: "good" },
  content: { label: "Content", emoji: "🙂", group: "good" },
  flat: { label: "Flat", emoji: "😐", group: "neutral" },
  low: { label: "Low", emoji: "😔", group: "low" },
  anxious: { label: "Anxious", emoji: "😰", group: "distressed" },
  distressed: { label: "Distressed", emoji: "😢", group: "distressed" },
};

export const MOOD_ORDER = Object.keys(MOODS);

const normalise = (mood) =>
  mood === null || mood === undefined
    ? null
    : String(mood).trim().toLowerCase() || null;

/** The mood's group — the API's `group` when it sent one, else the local table. */
export const moodGroup = (mood, apiGroup) => {
  if (apiGroup && MOOD_GROUPS[apiGroup]) return apiGroup;
  const m = normalise(mood);
  return (m && MOODS[m]?.group) || null;
};

/** { value, label, emoji, group, color, soft, tone } for a mood, or null. */
export const describeMood = (mood, apiGroup) => {
  const m = normalise(mood);
  if (!m) return null;
  const group = moodGroup(m, apiGroup);
  const g = group ? MOOD_GROUPS[group] : null;
  return {
    value: m,
    label:
      MOODS[m]?.label ||
      m.charAt(0).toUpperCase() + m.slice(1).replace(/_/g, " "),
    emoji: MOODS[m]?.emoji || null,
    group,
    color: g?.color || NEUTRAL_GREY,
    soft: g?.soft || "#F1F5F9",
    tone: g?.tone || "neutral",
  };
};

/**
 * Design-system Chip/Badge tone ("success" | "neutral" | "warning" | "danger")
 * for a mood's group — the resident card's vocabulary.
 */
export const moodBadgeTone = (mood, apiGroup) => {
  switch (moodGroup(mood, apiGroup)) {
    case "good":
      return "success";
    case "low":
      return "warning";
    case "distressed":
      return "danger";
    default:
      return "neutral";
  }
};
